import { isAgentRunExchangeTracked } from './agentRunRegistry';
import type { AssistantState } from './assistantStateOperations';
import type { PlanStatus } from './types';

export type RequestPendingStateInput = Pick<
  AssistantState,
  'chatSessionId' | 'currentActiveRequest'
> & {
  activeAgentRunSessionIds: ReadonlySet<string>;
  activePlanStatus: PlanStatus | null;
};

/**
 * A request the client started that the durable watch registry has not picked
 * up yet: the run is in flight but not yet resumable, so it counts as pending
 * and cannot be cancelled through the durable path.
 */
const hasUntrackedActiveRequest = ({
  currentActiveRequest,
}: RequestPendingStateInput): boolean =>
  currentActiveRequest !== null &&
  !isAgentRunExchangeTracked(currentActiveRequest);

export const getIsRequestPending = (
  input: RequestPendingStateInput
): boolean => {
  const { chatSessionId, activeAgentRunSessionIds, activePlanStatus } = input;

  if (activePlanStatus === 'proposed') return false;

  if (activePlanStatus === 'running' || activePlanStatus === 'replanning') {
    return true;
  }

  if (hasUntrackedActiveRequest(input)) return true;
  return chatSessionId !== null && activeAgentRunSessionIds.has(chatSessionId);
};

export const getIsCancelRequestPending = (
  input: RequestPendingStateInput & Pick<AssistantState, 'cancelling'>
): boolean => {
  const { cancelling } = input;

  if (hasUntrackedActiveRequest(input)) return true;
  return getIsRequestPending(input) && cancelling;
};
